import React, { useEffect, useState } from "react";

const TestingModal = ({ username, developerId, onClose }) => {
    const [projects, setProjects] = useState([]);
    const [selectedProject, setSelectedProject] = useState("");
    const [defects, setDefects] = useState([
        { description: "", severity: "" },
    ]);
    const [loading, setLoading] = useState(false);

    const BASE_URL = process.env.REACT_APP_BACKEND_URL;

    // ---------------- FETCH ALL PROJECTS (from all developer tasks) ----------------
    useEffect(() => {
        const fetchProjects = async () => {
            try {
                const res = await fetch(`${BASE_URL}/developer/projects`);
                if (!res.ok) throw new Error("Failed to fetch projects");
                const data = await res.json();

                // handle both shapes:
                // 1) ["Project A", "Project B"]  OR
                // 2) [{ name: "Project A" }, { name: "Project B" }]
                const names = Array.isArray(data)
                    ? data.map((p) => (typeof p === "string" ? p : p.name)).filter(Boolean)
                    : [];

                setProjects(names);
            } catch (err) {
                console.error("❌ Error fetching projects:", err);
            }
        };

        fetchProjects();
    }, [BASE_URL]);

    // ---------------- DEFECT HANDLERS ----------------
    const handleDefectChange = (index, field, value) => {
        const updated = [...defects];
        updated[index][field] = value;

        // 🧠 If "No Defect" is chosen, disable all other defect rows
        if (field === "severity" && value === "No Defect") {
            setDefects([{ description: "No issues found", severity: "No Defect" }]);
        } else if (field === "severity" && value !== "No Defect") {
            // Remove "No Defect" if user later adds real defects
            const filtered = updated.filter((d) => d.severity !== "No Defect");
            setDefects(filtered);
        } else {
            setDefects(updated);
        }
    };

    const handleAddDefect = () => {
        // Prevent adding if "No Defect" is already selected
        if (defects.some((d) => d.severity === "No Defect")) return;
        setDefects([...defects, { description: "", severity: "" }]);
    };

    const handleRemoveDefect = (index) => {
        setDefects(defects.filter((_, i) => i !== index));
    };

    // ---------------- SUBMIT TESTING ENTRY ----------------
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!selectedProject) {
            alert("Please select a project first.");
            return;
        }

        const hasNoDefect = defects.some((d) => d.severity === "No Defect");

        if (!hasNoDefect && defects.every((d) => !d.severity || !d.description)) {
            alert("Please add at least one valid defect or select 'No Defect'.");
            return;
        }

        const payload = {
            developer_id: developerId,
            developer_name: username,
            project_name: selectedProject,
            num_defects: hasNoDefect ? 0 : defects.length,
            defects: defects,
        };

        try {
            setLoading(true);
            const res = await fetch(`${BASE_URL}/developer/report-testing`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });

            if (!res.ok) {
                const errData = await res.json();
                throw new Error(errData.detail || "Failed to save testing entry");
            }

            showCenteredPopup("✅ Testing entry submitted successfully!", "success");
            onClose();
        } catch (err) {
            console.error("Error submitting testing entry:", err);
            showCenteredPopup("❌ Failed to submit testing entry", "error");
        } finally {
            setLoading(false);
        }
    };

    // ---------------- POPUP HELPER ----------------
    const showCenteredPopup = (message, type) => {
        const popup = document.createElement("div");
        popup.innerText = message;
        popup.style.position = "fixed";
        popup.style.top = "50%";
        popup.style.left = "50%";
        popup.style.transform = "translate(-50%, -50%)";
        popup.style.backgroundColor = type === "success" ? "#10b981" : "#ef4444";
        popup.style.color = "white";
        popup.style.padding = "16px 28px";
        popup.style.borderRadius = "10px";
        popup.style.fontWeight = "600";
        popup.style.fontSize = "1rem";
        popup.style.boxShadow = "0 0 20px rgba(0,0,0,0.4)";
        popup.style.zIndex = "9999";
        popup.style.transition = "opacity 0.3s ease";
        document.body.appendChild(popup);
        setTimeout(() => {
            popup.style.opacity = "0";
            setTimeout(() => document.body.removeChild(popup), 400);
        }, 1500);
    };

    // ---------------- JSX ----------------
    return (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-70 backdrop-blur-sm z-50">
            <div className="bg-[#1c1c1e] text-white rounded-2xl shadow-2xl p-8 w-full max-w-2xl relative border border-orange-500 max-h-[85vh] overflow-y-auto">
                {/* Close Button */}
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 text-gray-400 hover:text-red-400 transition text-xl"
                >
                    ✕
                </button>

                <h2 className="text-2xl font-semibold mb-6 text-center text-yellow-400">
                    🧪 Project Testing Entry
                </h2>

                <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Select Project */}
                    <div>
                        <label className="block text-sm mb-1 text-gray-300">Select Project</label>
                        <select
                            value={selectedProject}
                            onChange={(e) => setSelectedProject(e.target.value)}
                            className="w-full p-2 rounded-md bg-[#2a2a2d] border border-gray-600 text-white focus:ring-2 focus:ring-orange-500 outline-none"
                            required
                        >
                            <option value="">-- Select Project --</option>
                            {projects.map((proj, i) => (
                                <option key={i} value={proj}>
                                    {proj}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Defects Section */}
                    <div className="space-y-4">
                        {defects.map((def, index) => (
                            <div
                                key={index}
                                className="p-4 border border-gray-700 rounded-lg bg-[#2a2a2d]"
                            >
                                <div className="flex items-center justify-between mb-2">
                                    <h3 className="font-semibold text-yellow-400">
                                        Defect #{index + 1}
                                    </h3>
                                    {defects.length > 1 && (
                                        <button
                                            type="button"
                                            onClick={() => handleRemoveDefect(index)}
                                            className="text-red-400 hover:text-red-600 text-sm"
                                        >
                                            Remove
                                        </button>
                                    )}
                                </div>

                                <div className="flex gap-4">
                                    <textarea
                                        value={def.description}
                                        onChange={(e) =>
                                            handleDefectChange(index, "description", e.target.value)
                                        }
                                        placeholder="Describe the defect..."
                                        className="flex-1 p-2 rounded-md bg-[#1c1c1e] border border-gray-600 text-white focus:ring-2 focus:ring-orange-500 outline-none"
                                        rows="2"
                                        required={def.severity !== "No Defect"}
                                    />
                                    <select
                                        value={def.severity}
                                        onChange={(e) =>
                                            handleDefectChange(index, "severity", e.target.value)
                                        }
                                        className="w-40 p-2 rounded-md bg-[#1c1c1e] border border-gray-600 text-white focus:ring-2 focus:ring-orange-500 outline-none"
                                        required
                                    >
                                        <option value="">Severity</option>
                                        <option value="No Defect">No Defect</option>
                                        <option value="Low">Low</option>
                                        <option value="Medium">Medium</option>
                                        <option value="High">High</option>
                                        <option value="Critical">Critical</option>
                                    </select>
                                </div>
                            </div>
                        ))}

                        <button
                            type="button"
                            onClick={handleAddDefect}
                            className="px-3 py-1 bg-yellow-500 text-black rounded-md font-semibold hover:bg-yellow-400 transition"
                        >
                            + Add Another Defect
                        </button>
                    </div>

                    {/* Submit */}
                    <button
                        type="submit"
                        disabled={loading}
                        className={`w-full py-2 rounded-md font-semibold text-white transition duration-300 ${loading
                                ? "bg-gray-600 cursor-not-allowed"
                                : "bg-orange-500 hover:bg-yellow-500"
                            }`}
                    >
                        {loading ? "Submitting..." : "Submit Testing Entry"}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default TestingModal;
